import Link from 'next/link';
import { useState } from 'react';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { trpc } from '@/utils/trpc';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');

  const forgotPassword = trpc.user.forgotPassword.useMutation({
    onSuccess() {
      setEmail('');
    },
  });

  async function handleForgotPassword(event: React.FormEvent) {
    event.preventDefault();
    await forgotPassword.mutateAsync({ email });
  }

  return (
    <>
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-14 w-auto"
            src="/logo.png"
            alt="site logo"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Account Recovery
          </h2>
          <p className="mt-1 text-center font-medium text-gray-500">
            Request a password reset link for your account
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
            {forgotPassword.isError && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-red-700">
                      {forgotPassword.error.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {forgotPassword.isSuccess && (
              <div className="mb-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon
                      className="h-5 w-5 text-green-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-green-700">
                      A password reset link will be emailed to you shortly if an
                      account with that email exists. Please check your
                      junk/spam folder if it hasn&apos;t arrived after a few
                      minutes.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <form
              className="space-y-6"
              action="#"
              method="POST"
              onSubmit={handleForgotPassword}
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={forgotPassword.isLoading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {forgotPassword.isLoading
                    ? 'Loading...'
                    : 'Request password reset'}
                </button>
              </div>
            </form>

            <div className="mt-6 flex items-center justify-center">
              <div className="text-sm">
                <Link
                  href="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  &larr; Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
